import {
  ChangeDetectionStrategy,
  Component,
  computed,
  afterRenderEffect,
  inject,
  input,
  linkedSignal,
  signal,
} from '@angular/core';
import { criteoVideoPlayerFromUrl } from '@criteo/video-player';
import type {
  VideoErrorInfo,
  VideoOptions,
} from '@criteo/video-player/dist/types/src/components/video/video';
import {
  HmfPaginationCarouselComponent,
  HmfPrices,
  HmfProductData,
} from '@dsg-tech/homefield-angular';
import type {
  BeaconQueryParameters,
  CriteoProduct,
  CriteoRenderingAttributes,
  CriteoTrackingItem,
  CriteoVideo,
} from '@shared/data-access/interfaces';
import { CriteoService, DmnAdService } from '@shared/data-access/product-recommendations';
import { AnalyticsService, Logger } from '@shared/data-access/services';
import { FallbackSeoUrlService } from '@shared/utils/services';
import { CriteoVideoProductCardComponent } from '../criteo-video-product-card/criteo-video-product-card.component';

type CriteoVideoProductData = HmfProductData & {
  onClickBeacon: string;
  onBasketChangeBeacon: string;
  productId: string;
};

@Component({
  selector: 'shared-components-criteo-video',
  imports: [HmfPaginationCarouselComponent, CriteoVideoProductCardComponent],
  templateUrl: './criteo-video.component.html',
  styleUrl: './criteo-video.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CriteoVideoComponent {
  private readonly logger = inject(Logger);
  private readonly dmnAdService = inject(DmnAdService);
  private readonly criteoService = inject(CriteoService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly fallbackSeoUrlService = inject(FallbackSeoUrlService);
  private readonly currentProductIndex = signal(0);

  readonly videoPlacement = input.required<CriteoVideo>();
  readonly products = computed<CriteoVideoProductData[]>(() =>
    this.videoPlacement().products.map((criteoProduct) => {
      return this.mapCriteoProductToHmfProduct(criteoProduct);
    }),
  );

  private readonly videoFailed = linkedSignal(() => {
    this.videoPlacement();
    return false;
  });

  readonly showVideo = computed(
    () => !!this.videoPlacement().TagVideoVAST && this.products().length > 0 && !this.videoFailed(),
  );

  private readonly CRITEO_VIDEO_ELEMENT_ID = 'criteo-video-player';
  private readonly CRITEO_VIDEO_OPTIONS: VideoOptions = {
    altText: 'Sponsored Video Content',
    onError: (error: VideoErrorInfo) => {
      this.logger.error('Criteo Video Player Error:', error);
      this.videoFailed.set(true);
    },
  };

  private readonly EMPTY_PRICES: HmfPrices = {
    maxListPrice: '',
    maxOfferPrice: '',
    minListPrice: '',
    minOfferPrice: '',
    maxMapPrice: '',
    minMapPrice: '',
  };

  private readonly PRICE_ENTRY_REGEX =
    /'(maxlistprice|maxofferprice|minlistprice|minofferprice|maxmapprice|minmapprice)'\s*:\s*'([^']*)'/gi;

  private readonly QUICKVIEW_REFERRER = 'sponsored-product-ad';

  /**
   * @description
   * Criteo product tracking for DSG analytics, one entry per product in `videoPlacement().products`.
   */
  private criteoTracking: CriteoTrackingItem[] = [];

  /**
   * @description
   * Criteo tracking for the video placement itself (slot 0), used for the placement-level contentImpression.
   */
  private videoTrackingItem: CriteoTrackingItem | undefined;

  /**
   * @description
   * Criteo tracking metadata transformed for the product card data-em attr.
   */
  readonly criteoDataEms = signal<string[]>([]);

  constructor() {
    afterRenderEffect({
      write: () => {
        const placement = this.videoPlacement();
        if (!placement.TagVideoVAST || placement.products.length === 0) {
          return;
        }

        criteoVideoPlayerFromUrl(placement.TagVideoVAST, this.CRITEO_VIDEO_ELEMENT_ID, {
          ...this.CRITEO_VIDEO_OPTIONS,
          altText: placement.rendering.background_video_alt_text,
        })
          .then(() => this.observeVideoLoad())
          .catch((error) => {
            this.logger.error('Criteo Video Player failed to load:', error);
            this.videoFailed.set(true);
          });
        this.buildCriteoTracking(placement);
        this.setupCriteoViewObservers(placement);

        const beaconQueryParameters: BeaconQueryParameters = {
          placementType: 'VIDEO',
          placementLevel: 'PLACEMENT',
          beaconType: 'LOAD',
        };
        this.dmnAdService.sendBeacon(placement.OnLoadBeacon, beaconQueryParameters);
      },
    });
  }

  private observeVideoLoad(): void {
    const videoElement = document
      .getElementById(this.CRITEO_VIDEO_ELEMENT_ID)
      ?.querySelector('video');

    if (!(videoElement instanceof HTMLVideoElement)) {
      this.videoFailed.set(true);
      return;
    }

    const handleFailure = () => this.videoFailed.set(true);
    videoElement.addEventListener('error', handleFailure, { once: true });
    videoElement.addEventListener('abort', handleFailure, { once: true });
  }

  private getCriteoEventType(): string {
    switch (globalThis._dsgTag?.pageType?.Name) {
      case 'Family':
        return 'viewCategory';
      case 'Search':
        return 'viewSearchResult';
      case 'Product Detail':
        return 'viewItem';
      case 'Order Confirmation':
        return 'trackTransaction';
      default:
        return 'unknownPageType';
    }
  }

  private buildCriteoTracking(placement: CriteoVideo): void {
    const experience = globalThis._dsgTag?.experience ?? 'Desktop';
    const eventType = this.getCriteoEventType();
    const dataEms: string[] = [];

    this.criteoTracking = placement.products.map((_product, index) => {
      const trackingItem: CriteoTrackingItem = {
        vendor: 'Criteo',
        lineItemId: placement.LineItemID,
        placements: `${eventType}_API_${experience}_Video`,
        slot: index + 1,
      };
      dataEms.push(
        `Criteo:${trackingItem.lineItemId}:${trackingItem.placements}:${trackingItem.slot}`,
      );
      this.analyticsService.triggerAnalyticsEvent('contentRendered', trackingItem);
      return trackingItem;
    });
    this.criteoDataEms.set(dataEms);

    const videoTrackingItem: CriteoTrackingItem = {
      vendor: 'Criteo',
      lineItemId: placement.LineItemID,
      placements: `${eventType}_API_${experience}_Video`,
      slot: 0,
    };
    this.videoTrackingItem = videoTrackingItem;
    this.analyticsService.triggerAnalyticsEvent('contentRendered', videoTrackingItem);
  }

  /**
   * @description
   * Registers IntersectionObservers for the placement container and each product slide,
   * firing the OnView beacons plus a contentImpression event once each becomes visible.
   * Only the active carousel slide intersects, since inactive slides are clipped offscreen.
   */
  private setupCriteoViewObservers(placement: CriteoVideo): void {
    const playerElement = document.getElementById(this.CRITEO_VIDEO_ELEMENT_ID);
    if (playerElement) {
      this.criteoService.sponsoredProductsIntersectionObserver(playerElement, () => {
        this.dmnAdService.sendBeacon(placement.OnViewBeacon, {
          placementType: 'VIDEO',
          placementLevel: 'PLACEMENT',
          beaconType: 'VIEW',
        });
        this.analyticsService.triggerAnalyticsEvent('contentImpression', this.videoTrackingItem);
      });
    }

    placement.products.forEach((product, index) => {
      const productElement = document.getElementsByClassName(
        `criteo-video-${product.ProductId}`,
      )[0];
      if (!(productElement instanceof HTMLElement)) {
        return;
      }

      this.criteoService.sponsoredProductsIntersectionObserver(productElement, () => {
        this.dmnAdService.sendBeacon(product.OnViewBeacon, {
          placementType: 'VIDEO',
          placementLevel: 'PRODUCT',
          beaconType: 'VIEW',
        });
        this.analyticsService.triggerAnalyticsEvent(
          'contentImpression',
          this.criteoTracking[index],
        );
      });
    });
  }

  private mapCriteoProductToHmfProduct(criteoProduct: CriteoProduct): CriteoVideoProductData {
    const renderingAttr = this.parseRenderingAttributes(criteoProduct.RenderingAttributes);
    const pricesObject = this.parsePrices(renderingAttr?.rendering_attributes || '');
    return {
      productName: criteoProduct.ProductName,
      pricing: {
        prices: pricesObject,
      },
      starRatings: {
        ratingCount: renderingAttr?.numberOfReviews || '0',
        ratingValue: criteoProduct.Rating ? Number(criteoProduct.Rating) : 0,
        showCustomerRating: !!criteoProduct.Rating,
      },
      assetSeoUrl: this.fallbackSeoUrlService.buildSeoUrl(
        criteoProduct.ProductName,
        criteoProduct.ParentSKU,
      ),
      ecode: criteoProduct.ParentSKU,
      onBasketChangeBeacon: criteoProduct.OnBasketChangeBeacon,
      onClickBeacon: criteoProduct.OnClickBeacon,
      productId: criteoProduct.ProductId,
      promoMessage: criteoProduct.PromoText || '',
      imageData: {
        imageUrl: criteoProduct.Image,
        width: '200',
        height: '200',
        isLCPImage: true,
        isNgOptimizedImageSupported: true,
      },
    };
  }

  private parseRenderingAttributes(attributes: string): CriteoRenderingAttributes {
    try {
      return JSON.parse(attributes);
    } catch (error) {
      this.logger.error('Failed to parse Criteo rendering attributes', error);
      return {};
    }
  }

  private parsePrices(renderingAttributes: string): HmfPrices {
    if (!renderingAttributes) {
      return this.EMPTY_PRICES;
    }
    const prices: Record<string, string> = {};
    this.PRICE_ENTRY_REGEX.lastIndex = 0;
    for (const [, key, value] of renderingAttributes.matchAll(this.PRICE_ENTRY_REGEX)) {
      prices[key.toLowerCase()] = value;
    }

    return {
      maxListPrice: prices['maxlistprice'] || '',
      maxOfferPrice: prices['maxofferprice'] || '',
      minListPrice: prices['minlistprice'] || '',
      minOfferPrice: prices['minofferprice'] || '',
      maxMapPrice: prices['maxmapprice'] || '',
      minMapPrice: prices['minmapprice'] || '',
    };
  }

  // #region Click Handlers
  onAddToCartClicked(product: CriteoVideoProductData) {
    const beaconQueryParameters: BeaconQueryParameters = {
      placementType: 'VIDEO',
      placementLevel: 'PRODUCT',
      beaconType: 'BASKETCHANGE',
    };
    this.dmnAdService.sendBeacon(product.onBasketChangeBeacon, beaconQueryParameters);
    if (globalThis.quickview?.openProductQuickView) {
      globalThis.quickview.openProductQuickView(product.ecode, this.QUICKVIEW_REFERRER);
    }
  }

  onPageChanged(pageIndex: number) {
    this.currentProductIndex.set(pageIndex);
  }

  onCarouselClick(event: Event) {
    const product = this.getCarouselProduct(event.target);
    if (!product) {
      return;
    }

    this.navigateToProduct(product);
  }

  private getCarouselProduct(target: EventTarget | null): CriteoVideoProductData | null {
    if (!(target instanceof Element)) {
      return null;
    }
    // Prevent navigation when clicking on interactive elements within the carousel
    // including the flex space between nav arrows and pagination dots
    if (target.closest('button') || target.closest('.pagination-dots')) {
      return null;
    }

    return this.products()[this.currentProductIndex()] ?? null;
  }

  private navigateToProduct(product: CriteoVideoProductData) {
    const beaconQueryParameters: BeaconQueryParameters = {
      placementType: 'VIDEO',
      placementLevel: 'PRODUCT',
      beaconType: 'CLICK',
    };
    this.dmnAdService.sendBeacon(product.onClickBeacon, beaconQueryParameters);
    globalThis.location.href = product.assetSeoUrl;
  }
  // #endregion
}
